import type { ChauffeurPage } from "../slug-chauffeur";

import heroImage from "@/assets/images/chauffeur/cannes/chauffeur-cannes.png";
import { cityPriceFaq } from "./cityPricing";

const roquebruneCapMartin: ChauffeurPage = {
  slug: "roquebrune-cap-martin",
  title: "Chauffeur privé à Roquebrune-Cap-Martin – VTC 24h/24",
  subtitle:
    "Chauffeur privé et VTC à Roquebrune-Cap-Martin : transferts aéroport de Nice, dessertes Monaco et Menton, mise à disposition entre le village médiéval et le Cap-Martin, 7j/7.",
  keywords: [
    "chauffeur privé roquebrune-cap-martin",
    "vtc roquebrune-cap-martin",
    "vtc cap-martin",
    "transfert roquebrune aéroport nice",
  ],
  hero: {
    title: "Chauffeur privé & VTC à Roquebrune-Cap-Martin",
    subtitle:
      "Entre Monaco et Menton, du village médiéval perché aux villas du Cap-Martin : transferts aéroport de Nice (~40 min) et mise à disposition, 24h/24.",
    keywords: [
      "VTC Roquebrune-Cap-Martin",
      "chauffeur Cap-Martin",
      "transfert aéroport Nice",
      "desserte Monaco Menton",
    ],
    imageSrc: heroImage.src,
    imageAlt:
      "Chauffeur privé & VTC à Roquebrune-Cap-Martin, entre Monaco et Menton",
    badges: [
      "Aéroport de Nice ~40 min",
      "Desserte Monaco & Menton",
      "Chauffeurs bilingues",
    ],
  },
  benefits: {
    heading: "Pourquoi un chauffeur à Roquebrune-Cap-Martin",
    items: [
      {
        title: "Aéroport de Nice en ~40 min",
        description:
          "Liaison directe avec l’aéroport de Nice Côte d’Azur par l’A8 ou la Basse Corniche, suivi de vol et accueil inclus.",
      },
      {
        title: "Village perché, sans souci de stationnement",
        description:
          "Les ruelles en escaliers autour du château médiéval interdisent la voiture : dépose au plus près, reprise sur simple appel.",
      },
      {
        title: "Aux portes de Monaco et Menton",
        description:
          "Monte-Carlo à quelques minutes, Menton juste après la frontière : soirées, casinos et rendez-vous desservis sans conduire.",
      },
      {
        title: "Discrétion sur le Cap-Martin",
        description:
          "Villas et propriétés de la presqu’île : chauffeur ponctuel et discret, note 5,0/5 sur 38 avis Google.",
      },
    ],
  },
  services: {
    heading: "Nos services de chauffeur à Roquebrune-Cap-Martin",
    services: [
      {
        title: "Transfert aéroport de Nice",
        text: "Roquebrune-Cap-Martin ⇄ aéroport de Nice Côte d’Azur en ~40 min, avec suivi de vol et accueil personnalisé.",
      },
      {
        title: "Desserte Monaco & Menton",
        text: "Trajets courts vers Monte-Carlo, le port de Monaco ou le centre de Menton, aller simple ou attente sur place.",
      },
      {
        title: "Mise à disposition",
        text: "À l’heure ou à la journée, entre le village médiéval, le Cap-Martin et la Riviera, avec un chauffeur dédié.",
      },
      {
        title: "Culture & littoral",
        text: "Cabanon Le Corbusier, villa E-1027 et sentier du littoral : dépose et reprise aux accès du promontoire.",
      },
      {
        title: "Longue distance & gare",
        text: "Liaisons vers l’Italie voisine (Vintimille, San Remo) et transfert vers la gare de Roquebrune-Cap-Martin ou de Monaco-Monte-Carlo.",
      },
    ],
  },
  locations: {
    heading: "Lieux & points de prise en charge",
    items: [
      {
        name: "Village médiéval",
        description:
          "Château de Roquebrune et ruelles en escaliers, dépose au pied du promontoire.",
      },
      {
        name: "Cap-Martin",
        description:
          "Presqu’île résidentielle, villas et pinède face à la mer.",
      },
      {
        name: "Cabanon Le Corbusier & villa E-1027",
        description: "Site classé au bord du sentier du littoral.",
      },
      {
        name: "Gare de Roquebrune-Cap-Martin",
        description: "Arrêt de la ligne littorale Marseille–Vintimille.",
      },
      {
        name: "Frontière de Monaco",
        description: "Accès à Monte-Carlo et au port en quelques minutes.",
      },
      {
        name: "Aéroport de Nice",
        description: "Terminaux 1 et 2, à ~40 min par l’A8.",
      },
    ],
  },
  generic: {
    heading: "Un chauffeur entre village médiéval et Cap-Martin",
    subheading:
      "Deux visages à Roquebrune-Cap-Martin : le rocher médiéval au-dessus, la presqu’île chic en contrebas.",
    paragraphs: [
      "Roquebrune-Cap-Martin s’étire de son village médiéval perché — dominé par le plus ancien donjon de France — jusqu’à la presqu’île boisée du Cap-Martin et ses villas Belle Époque. KS Rent Services relie ces deux univers et dessert tout l’est de la Riviera avec un chauffeur privé, sans contrainte de stationnement dans les ruelles en escaliers.",
      "Coincée entre Monaco et Menton, la commune est idéalement placée : Monte-Carlo, son casino et son port sont à quelques minutes, Menton et la frontière italienne juste après. Nous assurons vos transferts vers l’aéroport de Nice Côte d’Azur en une quarantaine de minutes, avec suivi de vol et accueil personnalisé.",
      "Pour une visite du Cabanon Le Corbusier et de la villa E-1027, une promenade sur le sentier du littoral ou un dîner à Monaco, le chauffeur vous dépose au plus près et revient sur simple appel — vous profitez du Cap-Martin sans jamais chercher une place.",
    ],
    bullets: [
      "Aéroport de Nice en ~40 min",
      "Dépose au pied du village médiéval",
      "Monaco & Menton à quelques minutes",
    ],
  },
  process: {
    heading: "Réserver son chauffeur à Roquebrune-Cap-Martin",
    steps: [
      "Indiquez votre trajet (village, Cap-Martin, Monaco, Menton, aéroport) et vos horaires.",
      "Nous confirmons le véhicule adapté et le forfait, ou un devis immédiat.",
      "Le chauffeur suit votre vol et vous accueille à l’aéroport de Nice ou à l’adresse convenue.",
      "Dépose au plus près du promontoire et reprise sur simple appel.",
    ],
  },
  faq: [
    cityPriceFaq("fr", "Roquebrune-Cap-Martin"),
    {
      q: "Combien de temps pour rejoindre l’aéroport de Nice ?",
      a: "Comptez environ 40 minutes par l’A8 ou la Basse Corniche depuis Roquebrune-Cap-Martin, avec suivi de vol et accueil à l’arrivée. Le temps de trajet reste modéré, la commune étant à l’est de Nice.",
    },
    {
      q: "Desservez-vous Monaco et Menton ?",
      a: "Oui, Monte-Carlo et son port sont à quelques minutes, Menton juste après ; nous assurons aussi bien les allers simples que l’attente sur place pour une soirée ou un rendez-vous.",
    },
    {
      q: "Pouvez-vous monter jusqu’au village médiéval ?",
      a: "Les ruelles en escaliers autour du château sont piétonnes. Le chauffeur vous dépose au plus près de l’entrée du village et vous reprend au même endroit après la visite.",
    },
    {
      q: "Combien de temps à l’avance faut-il réserver ?",
      a: "Réservez au plus tôt en haute saison et lors des grands rendez-vous monégasques ; nous traitons aussi les demandes de dernière minute selon disponibilité.",
    },
    {
      q: "Vos chauffeurs parlent-ils anglais ?",
      a: "Oui, nos chauffeurs sont bilingues français/anglais, habitués à la clientèle internationale du Cap-Martin et de Monaco.",
    },
    {
      q: "Quels véhicules proposez-vous ?",
      a: "Berline Mercedes Classe E et Classe S, van, SUV et minibus VIP, selon le nombre de passagers et l’occasion.",
    },
  ],
  relatedLinks: [
    { label: "Voiture avec chauffeur à Monaco", href: "/chauffeur/monaco" },
    { label: "Voiture avec chauffeur à Nice", href: "/chauffeur/nice" },
    {
      label: "Service de chauffeur sur la Riviera",
      href: "/chauffeur/cote-d-azur",
    },
    {
      label: "Tarifs de location à Monaco",
      href: "/location/location-voiture-monaco",
    },
  ],
  cta: {
    title: "Votre chauffeur privé à Roquebrune-Cap-Martin",
    text: "Transfert aéroport de Nice, desserte Monaco-Menton ou mise à disposition : réponse rapide, 7j/7.",
    primaryHref: "/reservation",
    primaryLabel: "Réserver / demander un devis",
  },
  seo: {
    title: "Chauffeur privé Roquebrune-Cap-Martin — VTC & transferts",
    description:
      "Chauffeur privé et VTC à Roquebrune-Cap-Martin : transferts aéroport de Nice (~40 min), desserte Monaco et Menton, entre village médiéval et Cap-Martin.",
    canonical: "/chauffeur/roquebrune-cap-martin",
  },
};

export default roquebruneCapMartin;
